import { useMutation } from 'react-query';

import type { LoadFileDelimiters } from '../../../../backend/src/integrations/relativity/load-file-import/concordance-dat-parser';
import { APIBaseChronos } from '@/api/hosts';
import useGetFetchConfig, { FetchMethod } from '@/api/useGetFetchConfig';

/** One column's import mapping, mirroring the backend `LoadFileColumnMappingDto`. */
export interface LoadFileColumnMappingInput {
  header: string;
  fieldKey: string;
  displayName: string;
  fieldType: string;
  isArray?: boolean;
  arraySeparator?: string;
  enabled: boolean;
  isIdentifier?: boolean;
}

/** Filename-matching options, mirroring the backend `LoadFileMatchSettings`. */
export interface LoadFileMatchSettings {
  /** Strip the file extension from the load file column value before matching. */
  stripIncomingExtensions: boolean;
  /** Strip the file extension from Wexler's document filenames before matching. */
  stripCurrentExtensions: boolean;
}

interface StartLoadFileImportParams {
  matterId: string;
  importId: string;
  filenameColumnHeader: string;
  matchSettings: LoadFileMatchSettings;
  columnMapping: LoadFileColumnMappingInput[];
  delimiters: LoadFileDelimiters;
}

interface StartLoadFileImportResponse {
  jobId: string;
}

/** Validates the confirmed mapping and enqueues the import orchestrator. */
export const useStartLoadFileImport = () => {
  const { getFetchConfig } = useGetFetchConfig();

  return useMutation<StartLoadFileImportResponse, Error, StartLoadFileImportParams>(
    async ({ matterId, importId, ...body }) => {
      const url = `${APIBaseChronos}/api/relativity/load-file-import/${importId}/start?matterId=${matterId}`;
      const response = await fetch(url, getFetchConfig({ method: FetchMethod.POST, data: body }));

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to start import');
      }

      return response.json();
    },
  );
};

export default useStartLoadFileImport;
